import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { Observable } from 'rxjs';
import type { TableModel } from 'src/models/TableModel';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type {
    ConnectionStatusRefetchEvent,
    ConnectionStatusSignal,
    TableRowCountObservableSignal,
    TableRowCountSignal,
    IsScrollingTableSignal,
    TableTotalRowCountSignal,
} from 'src/types/EventBus/index';

export type WidgetContainerTableSliceSignals = ConnectionStatusSignal &
    IsScrollingTableSignal &
    TableRowCountSignal &
    TableRowCountObservableSignal &
    TableTotalRowCountSignal;

export type WidgetContainerTableSliceEvents = ConnectionStatusRefetchEvent;

export type WidgetContainerTableSliceEventBus = EventBus<
    WidgetContainerTableSliceSignals,
    WidgetContainerTableSliceEvents
>;

interface WidgetContainerTableSliceInit {
    table: TableModel;
}

export interface WidgetContainerTableSliceModel {
    readonly eventBus: WidgetContainerTableSliceEventBus;
}

export const WidgetContainerTableSlice: ModelConstructor<
    WidgetContainerTableSliceModel,
    [WidgetContainerTableSliceInit]
> = createModel<WidgetContainerTableSliceModel, [WidgetContainerTableSliceInit]>(
    (init: WidgetContainerTableSliceInit) => {
        const tableRowCountObservable = signal<Observable<number> | undefined>(init.table.count$);
        const tableTotalRowCount = signal<number | null>(null);

        const connectionStatus: ReadonlySignal<ConnectionStatusSignal['widget::connectionStatus']['value']> = computed(
            () => ({
                status: init.table.connectionStatus.value,
                progress: init.table.progress.value,
                failureCount: init.table.failureCount.value,
            }),
        );

        const eventBus: WidgetContainerTableSliceEventBus = EventBusBuilder.create()
            .addSignal('widget::connectionStatus', () => connectionStatus)
            .addSignal('widget::tableRowCount', () => init.table.countSignal)
            .addSignal('widget::isScrollingTable', () => init.table.isScrolling)
            .addSignal('widget::tableRowCountObservable', () => tableRowCountObservable)
            .addSignal('widget::tableTotalRowCount', () => tableTotalRowCount)
            .addEvent('widget::connectionStatusRefetch', () => {
                init.table.refetch();
            })
            .build();

        return {
            eventBus,
        };
    },
);

export type WidgetContainerTableSlice = Model<WidgetContainerTableSliceModel>;
